import { ParagonCrmProvider } from "components/integrations/managed_integrations/crmProviders";
import { MappableField } from "lib/generated/integration-api";
import { acceptsCrmField } from "./fieldCompatibility";
import { CrmRecordKind, RecordFieldSpec } from "./recordFields";

/**
 * What a section is filled in with before anyone has configured it, for one record going to one
 * CRM object.
 *
 * The provider's table says what usually belongs where; this decides how much of it the customer's
 * own CRM can actually take. A pair naming a field their org does not have, or one of a type that
 * cannot hold the value, is worse than no suggestion at all — it is a mapping that fails on the
 * first push, long after whoever accepted it has moved on.
 */
export function defaultMappingFor(
  provider: ParagonCrmProvider,
  kind: CrmRecordKind,
  entityName: string,
  /** The application fields this record and object offer, with their references already resolved. */
  specs: RecordFieldSpec[],
  /** What the CRM reports for the object, which is the only authority on what it has. */
  crmFields: MappableField[],
): Record<string, string> {
  const suggested = provider.defaultFields?.[kind]?.[entityName];
  if (!suggested) return {};

  const specByKey = new Map(specs.map((spec) => [spec.key, spec]));
  const fieldById = new Map(crmFields.map((field) => [field.id, field]));

  const mapping: Record<string, string> = {};
  const taken = new Set<string>();

  for (const [key, crmFieldId] of Object.entries(suggested)) {
    // Not offered for this record and object — a person's fields where the object holds none.
    const spec = specByKey.get(key);
    if (!spec) continue;

    // The CRM does not have it. Org by org this is the common case, and the reason the table is a
    // suggestion rather than something that could be written without asking the CRM first.
    const field = fieldById.get(crmFieldId);
    if (!field) continue;

    if (!acceptsCrmField(spec, field)) continue;

    // Two application fields pointed at one CRM field is refused on save, so a table that named
    // the same field twice would make the whole section unsavable.
    if (taken.has(crmFieldId)) continue;

    mapping[key] = crmFieldId;
    taken.add(crmFieldId);
  }

  return mapping;
}
